import { useState, useEffect } from 'react';
import PageMeta from '../components/PageMeta';
import OrderForm from '../components/OrderForm';
import {
  fetchSide,
  fetchVirksomhedsoplysninger,
  FALLBACK_SIDER,
  FALLBACK_VIRKSOMHED
} from '../lib/cms';

export default function Kontakt() {
  const [sideData, setSideData] = useState(FALLBACK_SIDER.kontakt || {});
  const [virksomhed, setVirksomhed] = useState(FALLBACK_VIRKSOMHED);

  useEffect(() => {
    async function loadData() {
      const [sRes, vRes] = await Promise.all([
        fetchSide('kontakt'),
        fetchVirksomhedsoplysninger()
      ]);
      if (sRes) setSideData(sRes);
      if (vRes) setVirksomhed(vRes);
    }
    loadData();
  }, []);
  return (
    <div className="bg-slate-50 min-h-screen">
      <PageMeta
        title={sideData.titel || "Kontakt - Duus Rustfri"}
        description={sideData.meta_beskrivelse || `Kontakt Duus Rustfri på ${virksomhed.adresse}, ${virksomhed.postnr_by}. Tlf: ${virksomhed.telefon}. Email: ${virksomhed.email}. Få et uforpligtende tilbud på specialfremstilling og svejsning.`}
      />

      {/* Hero Header */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-4">
              <i className="fa-solid fa-address-card text-sky-400" aria-hidden="true" />
              <span>{sideData.badge || 'Direkte kontakt & tilbud'}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4">
              {sideData.overskrift || 'Kontakt Duus Rustfri'}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              {sideData.underoverskrift || 'Uanset om du har en konkret forespørgsel, brug for en hurtig reparation eller vil have rådgivning om stål og svejsning, er du altid velkommen til at række ud.'}
            </p>
          </div>
        </div>
      </section>

      {/* Main Grid: Contact Info Cards + Form */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Direct Info & Location */}
            <div className="lg:col-span-5 space-y-8">
              {/* Contact Card */}
              <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-950 mb-1">
                    {virksomhed.firmanavn}
                  </h2>
                  <p className="text-xs uppercase font-mono tracking-wider text-slate-500">
                    {virksomhed.undertitel || 'Enkeltmandsvirksomhed · Rustfri, Sort & Alu'}
                  </p>
                </div>

                <div className="space-y-4 pt-2 border-t border-slate-100">
                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-sky-600 flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="block text-xs uppercase font-bold text-slate-500">
                        Værksted & Adresse
                      </span>
                      <p className="text-sm font-semibold text-slate-900">
                        {virksomhed.adresse}
                      </p>
                      <p className="text-sm text-slate-600">
                        {virksomhed.postnr_by}
                      </p>
                    </div>
                  </div>

                  {/* Telephone */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-sky-600 flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="block text-xs uppercase font-bold text-slate-500">
                        Telefon
                      </span>
                      <a
                        href={`tel:${virksomhed.telefon.replace(/\s+/g, '')}`}
                        className="text-base font-bold text-sky-700 hover:text-sky-800 transition whitespace-nowrap block"
                      >
                        {virksomhed.telefon}
                      </a>
                      <span className="text-xs text-slate-500">
                        Ring gerne for en uforpligtende snak
                      </span>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-sky-600 flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="block text-xs uppercase font-bold text-slate-500">
                        Email
                      </span>
                      <a
                        href={`mailto:${virksomhed.email}`}
                        className="text-base font-bold text-sky-700 hover:text-sky-800 transition whitespace-nowrap block"
                      >
                        {virksomhed.email}
                      </a>
                      <span className="text-xs text-slate-500">
                        Svarer hurtigst muligt på alle mails
                      </span>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-sky-600 flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-building" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="block text-xs uppercase font-bold text-slate-500">
                        CVR-nummer
                      </span>
                      <p className="text-sm font-mono font-semibold text-slate-900 whitespace-nowrap">
                        {virksomhed.cvr}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map embed / Location box */}
              <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-950 text-sm uppercase tracking-wide">
                    Find værkstedet
                  </h3>
                  <span className="text-xs text-slate-500 font-mono">Vissenbjerg, Fyn</span>
                </div>

                <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-slate-100 border border-slate-200 relative">
                  <iframe
                    title="Kort over Duus Rustfri på Bakkevej 17, 5492 Vissenbjerg"
                    src="https://maps.google.com/maps?q=Bakkevej%2017,%205492%20Vissenbjerg&t=&z=14&ie=UTF8&iwloc=&output=embed"
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Værkstedet er centralt beliggende i Vissenbjerg med nem tilkørsel fra den fynske motorvej (E20).
                </p>
              </div>

              {/* FAQ box */}
              <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 border border-slate-800 space-y-4">
                <h3 className="font-bold text-lg text-white">Ofte stillede spørgsmål</h3>

                <div className="space-y-3 text-xs sm:text-sm">
                  <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                    <p className="font-semibold text-sky-300 mb-1">Kan I give en fast pris på forhånd?</p>
                    <p className="text-slate-400">Ja, sender du en tegning eller beskrivelse med mål, udregner vi gerne et fast og uforpligtende tilbud.</p>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                    <p className="font-semibold text-sky-300 mb-1">Laver I opgaver for private?</p>
                    <p className="text-slate-400">Ja, vi hjælper både privatpersoner, landmænd og erhvervsvirksomheder med alle typer smedearbejde.</p>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                    <p className="font-semibold text-sky-300 mb-1">Hvor hurtigt kan I levere?</p>
                    <p className="text-slate-400">Små reparationer klares ofte fra dag til dag. Større specialproduktioner aftales individuelt efter dine deadlines.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Order & Inquiry Form */}
            <div className="lg:col-span-7">
              <OrderForm
                title="Send en bestillingsforespørgsel"
                subtitle="Udfyld felterne nedenfor med detaljer om din opgave. Du kan også vedhæfte fotos, skitser eller tegninger."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
